/**
 * UUIDv7 — time-sortable identifiers, generated on the client.
 *
 * The server never mints an id for a terminal-originated record (Principle I).
 * v7 is used rather than v4 because the leading 48 bits are a millisecond
 * timestamp, which gives records a stable creation ordering that does not
 * depend on a terminal's clock being correct relative to any other terminal —
 * a property the sync protocol leans on for ordering envelopes.
 */

const HEX: string[] = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, "0"));

/** Monotonic guard: two ids minted in the same millisecond must still order. */
let lastTimestamp = -1;
let sequence = 0;

export function uuidv7(now: number = Date.now()): string {
  if (now === lastTimestamp) {
    sequence += 1;
  } else {
    lastTimestamp = now;
    sequence = 0;
  }

  const bytes = new Uint8Array(16);

  // 48-bit big-endian millisecond timestamp
  bytes[0] = (now / 2 ** 40) & 0xff;
  bytes[1] = (now / 2 ** 32) & 0xff;
  bytes[2] = (now / 2 ** 24) & 0xff;
  bytes[3] = (now / 2 ** 16) & 0xff;
  bytes[4] = (now / 2 ** 8) & 0xff;
  bytes[5] = now & 0xff;

  crypto.getRandomValues(bytes.subarray(6));

  // Fold the intra-millisecond sequence into rand_a so same-ms ids stay ordered.
  bytes[6] = 0x70 | ((sequence >> 8) & 0x0f); // version 7
  bytes[7] = sequence & 0xff;
  bytes[8] = 0x80 | (bytes[8]! & 0x3f); // variant 10

  const h = (i: number) => HEX[bytes[i]!]!;
  return (
    h(0) + h(1) + h(2) + h(3) + "-" +
    h(4) + h(5) + "-" +
    h(6) + h(7) + "-" +
    h(8) + h(9) + "-" +
    h(10) + h(11) + h(12) + h(13) + h(14) + h(15)
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Milliseconds encoded in a v7 id. Useful for archival windows and debugging. */
export function uuidv7Timestamp(id: string): number {
  const hex = id.replace(/-/g, "").slice(0, 12);
  return Number.parseInt(hex, 16);
}
